import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPullRequests } from '../api';
import { Badge, Empty, ErrorBox, Loading, RelativeTime, Section, Table } from '../components/ui';

const DECISION: Record<string, { label: string; tone: 'good' | 'warn' | 'bad' | 'neutral' }> = {
  APPROVED: { label: 'aprobado', tone: 'good' },
  CHANGES_REQUESTED: { label: 'cambios pedidos', tone: 'bad' },
};

export function PullRequestsPage(): JSX.Element {
  const [state, setState] = useState<'open' | 'merged'>('open');
  const query = useQuery({
    queryKey: ['pull-requests', state],
    queryFn: () => fetchPullRequests(state),
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const all = query.data;
  const humans = all.filter((pr) => !pr.is_bot_author);
  const bots = all.filter((pr) => pr.is_bot_author);
  const reviewed = all.filter((pr) => pr.review_count > 0).length;

  return (
    <>
      <h1 className="mb-1 text-lg font-semibold">Pull requests</h1>
      <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
        {humans.length} de personas y {bots.length} de bots.{' '}
        {reviewed === 0
          ? 'Ninguno tiene reseñas de código registradas.'
          : `${reviewed} con reseñas.`}
      </p>

      <div className="mb-6 flex gap-1">
        {(['open', 'merged'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setState(option)}
            className={`rounded px-2.5 py-1 text-[12px] ${
              state === option
                ? 'bg-[var(--color-surface-muted)] font-medium'
                : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {option === 'open' ? 'Abiertos' : 'Fusionados'}
          </button>
        ))}
      </div>

      <Section
        title="De personas"
        subtitle={state === 'open' ? 'Ordenados por antigüedad: los más viejos arriba' : undefined}
      >
        {humans.length === 0 ? (
          <Empty>Ninguno.</Empty>
        ) : (
          <PrTable rows={humans} showAge={state === 'open'} />
        )}
      </Section>

      <Section
        title="De bots"
        subtitle="Separados a propósito: si no, distorsionan el indicador de pull requests abiertos"
      >
        {bots.length === 0 ? (
          <Empty>Ninguno.</Empty>
        ) : (
          <PrTable rows={bots} showAge={state === 'open'} />
        )}
      </Section>
    </>
  );
}

function PrTable({
  rows,
  showAge,
}: {
  rows: Awaited<ReturnType<typeof fetchPullRequests>>;
  showAge: boolean;
}): JSX.Element {
  return (
    <Table
      head={[
        '#',
        'Título',
        'Repositorio',
        'Autor',
        'Rama',
        'Diff',
        'Revisión',
        showAge ? 'Antigüedad' : 'Fusionado',
      ]}
    >
      {rows.map((pr) => {
        const decision = pr.review_decision ? DECISION[pr.review_decision] : undefined;
        return (
          <tr
            key={`${pr.repo}#${pr.number}`}
            className="border-b border-[var(--color-border-subtle)] last:border-0"
          >
            <td className="px-3 py-2 tabular-nums text-[var(--color-ink-muted)]">{pr.number}</td>
            <td className="px-3 py-2">
              {pr.html_url ? (
                <a href={pr.html_url} target="_blank" rel="noreferrer" className="hover:underline">
                  {pr.title}
                </a>
              ) : (
                pr.title
              )}
              {pr.is_draft && (
                <span className="ml-1.5">
                  <Badge>borrador</Badge>
                </span>
              )}
            </td>
            <td className="px-3 py-2 text-[12px] text-[var(--color-ink-muted)]">{pr.repo}</td>
            <td className="px-3 py-2 text-[12px]">{pr.author ?? '—'}</td>
            <td className="px-3 py-2 font-mono text-[11px]">
              {pr.head_ref} → {pr.base_ref}
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-[11px] tabular-nums">
              {pr.additions === null ? (
                '—'
              ) : (
                <>
                  <span className="text-emerald-700 dark:text-emerald-400">+{pr.additions}</span>{' '}
                  <span className="text-red-700 dark:text-red-400">−{pr.deletions ?? 0}</span>
                </>
              )}
            </td>
            <td className="whitespace-nowrap px-3 py-2">
              {decision ? (
                <Badge tone={decision.tone}>{decision.label}</Badge>
              ) : pr.review_count > 0 ? (
                <Badge>{pr.review_count} reseña(s)</Badge>
              ) : (
                <span
                  className="text-[11px] text-[var(--color-ink-muted)]"
                  title="Nadie ha revisado este pull request"
                >
                  sin revisar
                </span>
              )}
            </td>
            <td className="whitespace-nowrap px-3 py-2">
              {showAge ? (
                pr.ageDays === null ? (
                  '—'
                ) : (
                  <span
                    className={pr.ageDays > 7 ? 'text-amber-700 dark:text-amber-400' : undefined}
                  >
                    {pr.ageDays} d
                  </span>
                )
              ) : (
                <RelativeTime iso={pr.merged_at} />
              )}
            </td>
          </tr>
        );
      })}
    </Table>
  );
}
